import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { CloudUpload, Lock } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import {
  createUploadSession,
  addFile,
  setEncryptionEnabled,
} from '../store/slices/uploadSlice';
import FileList from '../components/FileList';
import UploadProgress from '../components/UploadProgress';
import { EncryptionService } from '../services/encryptionService';
import { sanitizeText, sanitizeError } from '../utils/sanitize';

// SECURITY: encryptionPassword is kept in component-local state only and is
// never dispatched to the Redux store, preventing it from appearing in DevTools
// or serialised state snapshots. (supportlogsio-1-196 L-1)
const UploadPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { sessionId, caseNumber, isEncryptionEnabled, loading, error } = useSelector(
    (state: RootState) => state.upload
  );
  const [caseInput, setCaseInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // generatedPassword is component-local — intentionally not in Redux.
  const [generatedPassword, setGeneratedPassword] = useState('');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        dispatch(
          addFile({
            id: `${file.name}-${Date.now()}`,
            name: file.name,
            size: file.size,
          })
        );
      });
    },
    [dispatch]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: !sessionId,
  });

  const handleStartSession = async () => {
    if (caseInput) {
      await dispatch(createUploadSession(caseInput));
    }
  };

  const handleEncryptionToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    dispatch(setEncryptionEnabled(enabled));

    if (enabled && !generatedPassword) {
      const password = EncryptionService.generateRandomPassword();
      setGeneratedPassword(password);
      setShowPassword(true);
    }
  };

  if (!sessionId) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Secure File Upload
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{sanitizeError(error)}</Alert>}

          <TextField
            fullWidth
            label="Case Number"
            value={caseInput}
            onChange={(e) => setCaseInput(e.target.value)}
            margin="normal"
            placeholder="e.g., CS-2024-001"
          />

          <FormControlLabel
            control={
              <Switch
                checked={isEncryptionEnabled}
                onChange={handleEncryptionToggle}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Lock sx={{ mr: 1 }} />
                Enable Client-Side Encryption
              </Box>
            }
            sx={{ mt: 2 }}
          />

          {isEncryptionEnabled && showPassword && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Save this password securely. You'll need it to decrypt files:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: '#f5f5f5',
                  p: 1,
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                {sanitizeText(generatedPassword)}
              </Typography>
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleStartSession}
            disabled={!caseInput || loading}
            sx={{ mt: 3 }}
          >
            {loading ? 'Creating Session...' : 'Start Upload Session'}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Upload Files for Case {sanitizeText(caseNumber)}
      </Typography>

      <Paper
        {...getRootProps()}
        elevation={3}
        sx={{
          p: 4,
          mt: 2,
          backgroundColor: isDragActive ? '#f0f0f0' : 'background.paper',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          cursor: 'pointer',
          textAlign: 'center',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: '#fafafa',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
        <Typography variant="h6">
          {isDragActive
            ? 'Drop files here...'
            : 'Drag & drop files here, or click to select'}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {isEncryptionEnabled
            ? 'Files will be encrypted before upload'
            : 'Upload files securely to support'}
        </Typography>
      </Paper>

      <FileList />
      {/* Pass encryptionPassword as a prop — it never enters Redux state. */}
      <UploadProgress encryptionPassword={generatedPassword || null} />
    </Container>
  );
};

export default UploadPage;
